// libs/core/src/jmkw.module.ts

import { APP_BASE_HREF, CommonModule, DatePipe } from '@angular/common';
import { provideHttpClient } from '@angular/common/http';
import {
  inject,
  ModuleWithProviders,
  NgModule,
  provideAppInitializer
} from '@angular/core';

// Components
import { LegendsComponent } from './components/legend/legends.component';
import { ToolbarComponent } from './components/toolbar/toolbar.component';
import { WidgetBarComponent } from './components/widget-bar/widget-bar.component';
import { JmkwComponent } from './jmkw.component';

// Modules and Utilities
import { DialogModule } from '@angular/cdk/dialog';
import { JmkwEnvironment } from '@jeppesen-foreflight/jmkw-types';
import {
  LoggerModule,
  NGXLogger,
  NgxLoggerLevel,
  TOKEN_LOGGER_CONFIG
} from 'ngx-logger';
import { CONFIG_TOKEN } from './jmkw.config';
import { configureSignalStore } from './jmkw.store';
import { MapViewComponent } from './lib/arcgis/map-view/map-view.component';
import { buildLoggerConfig, configureLogLevel } from './lib/logger/logger-config';
import { initializeRuntimeSequence, PurlyService } from './lib/services';

const PROVIDERS = [DatePipe, PurlyService];

@NgModule({
  declarations: [JmkwComponent],
  imports: [
    CommonModule,
    DialogModule,
    LoggerModule.forRoot({
      level: NgxLoggerLevel.DEBUG,
      serverLogLevel: NgxLoggerLevel.ERROR
    }),
    MapViewComponent,
    LegendsComponent,
    ToolbarComponent,
    WidgetBarComponent
  ],
  providers: PROVIDERS,
  exports: [CommonModule, LoggerModule, DialogModule, JmkwComponent]
})
export class JmkwModule {
  static forRoot(
    jmkwEnvironmentConfig: Partial<JmkwEnvironment>
  ): ModuleWithProviders<JmkwModule> {
    return {
      ngModule: JmkwModule,
      providers: [
        { provide: CONFIG_TOKEN, useValue: jmkwEnvironmentConfig },

        // HTTP Client for services
        provideHttpClient(),

        provideAppInitializer(() => {
          const config = inject(CONFIG_TOKEN);
          const initFn = configureSignalStore(config);
          return initFn();
        }),

        provideAppInitializer(() => {
          const config = inject(CONFIG_TOKEN);
          const logger = inject(NGXLogger);
          const initFn = configureLogLevel(config, logger);
          return initFn();
        }),

        // Sequences runtime config loading and Purly initialization.
        // These must run in order — see runtime-sequence.initializer.ts for details.
        provideAppInitializer(() => {
          const initFn = initializeRuntimeSequence();
          return initFn();
        }),

        {
          provide: APP_BASE_HREF,
          useValue: jmkwEnvironmentConfig.base_href ?? '/'
        },

        // Override TOKEN_LOGGER_CONFIG with a factory that reads localStorage and
        // JmkwEnvironment.logLevel at DI resolution time — before any NGXLogger is created.
        // This must come after JmkwModule's @NgModule.imports (which registers the default
        // LoggerModule.forRoot config) so that this factory wins as the last registration.
        {
          provide: TOKEN_LOGGER_CONFIG,
          useFactory: buildLoggerConfig,
          deps: [CONFIG_TOKEN]
        }
      ]
    };
  }
}
